import React, { useState, useEffect } from 'react';
import { getScannerSettings, saveScannerSettings, type ScannerSettings as ScannerSettingsType } from '../services/settingsService';
import { logger } from '../services/logger';
import { Spinner } from './Spinner';
import { COLOR_EFFECTS, ColorEffect } from '../types';

interface ScannerSettingsProps {
    title: string;
}

const RESOLUTION_OPTIONS = [
    { label: 'Výchozí nastavení systému', value: null },
    { label: 'HD (1280x720)', value: { width: 1280, height: 720 } },
    { label: 'Full HD (1920x1080)', value: { width: 1920, height: 1080 } },
    { label: '4K UHD (3840x2160)', value: { width: 3840, height: 2160 } },
];

export const ScannerSettings: React.FC<ScannerSettingsProps> = ({ title }) => {
    const [settings, setSettings] = useState<ScannerSettingsType | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    
    useEffect(() => {
        getScannerSettings().then(setSettings);
    }, []);

    const triggerSuccessMessage = () => {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
    };

    const handleResolutionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;
        const resolution = selectedValue === 'null' ? null : JSON.parse(selectedValue);
        setSettings(prev => {
            if (!prev) return null;
            const newSettings = { ...prev, resolution };
            saveScannerSettings(newSettings)
                .then(triggerSuccessMessage)
                .catch(err => logger.error("Failed to save scanner settings", err));
            return newSettings;
        });
    };

    const handleQualityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const jpegQuality = parseFloat(e.target.value);
        setSettings(prev => prev ? { ...prev, jpegQuality } : null);
    };

    const handleQualityMouseUp = () => {
        if (settings) {
            saveScannerSettings(settings)
                .then(triggerSuccessMessage)
                .catch(err => logger.error("Failed to save scanner settings", err));
        }
    };

    const handleEffectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newEffect = e.target.value as ColorEffect;
        setSettings(prev => {
            if (!prev) return null;
            const newSettings = { ...prev, defaultPaperSnapEffect: newEffect };
            saveScannerSettings(newSettings)
                .then(triggerSuccessMessage)
                .catch(err => logger.error("Failed to save scanner settings", err));
            return newSettings;
        });
    };


    if (!settings) {
        return <Spinner text="Načítám nastavení skeneru..." />;
    }

    const resolutionValue = settings.resolution ? JSON.stringify(settings.resolution) : 'null';
    const qualityPercentage = Math.round(settings.jpegQuality * 100);

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">{title}</h3>
            <p className="text-sm text-gray-600 mb-6">
                Optimalizujte kvalitu a velikost naskenovaných účtenek. Vyšší kvalita může vést k větším souborům PDF.
            </p>

            <div className="space-y-6 max-w-md">
                <div>
                    <label htmlFor="resolution-select" className="block text-sm font-medium text-gray-700 mb-2">
                        Rozlišení kamery
                    </label>
                    <select
                        id="resolution-select"
                        value={resolutionValue}
                        onChange={handleResolutionChange}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        {RESOLUTION_OPTIONS.map(opt => (
                            <option key={opt.label} value={opt.value ? JSON.stringify(opt.value) : 'null'}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        Vyšší rozlišení poskytuje ostřejší obraz, ale nemusí být podporováno všemi zařízeními.
                    </p>
                </div>

                <div>
                    <label htmlFor="quality-slider" className="block text-sm font-medium text-gray-700 mb-2">
                        Kvalita komprese obrázku ({qualityPercentage}%)
                    </label>
                    <input
                        id="quality-slider"
                        type="range"
                        min="0.1"
                        max="1.0"
                        step="0.05"
                        value={settings.jpegQuality}
                        onChange={handleQualityChange}
                        onMouseUp={handleQualityMouseUp}
                        onTouchEnd={handleQualityMouseUp}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                     <p className="text-xs text-gray-500 mt-1">
                        Nižší kvalita znamená menší soubory, 100% je nejlepší kvalita. Doporučeno: 90-100%.
                    </p>
                </div>

                <div>
                    <label htmlFor="effect-select" className="block text-sm font-medium text-gray-700 mb-2">
                        Výchozí efekt pro vylepšení skenu (PaperSnap)
                    </label>
                    <select
                        id="effect-select"
                        value={settings.defaultPaperSnapEffect}
                        onChange={handleEffectChange}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        {COLOR_EFFECTS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        Tento efekt bude automaticky předvybrán v editoru skenů.
                    </p>
                </div>

                 {showSuccess && (
                    <div className="text-green-600 font-medium text-sm transition-opacity duration-300" role="status">
                        Nastavení uloženo!
                    </div>
                )}
            </div>
        </div>
    );
};